import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { errorMessage } from '@/shared/api/httpClient'
import { roomsApi } from '@/shared/api/roomsApi'
import { useAsync } from '@/shared/api/useAsync'
import { Blueprint, Corners } from '@/shared/ui/Blueprint'
import { Dialog } from '@/shared/ui/Dialog'
import { devicesApi } from '@/features/devices/devicesApi'
import type { Device, Kind, Modo } from '@/features/devices/types'

const KELVINS = [
  { kelvin: 2200, label: 'Vela', color: '#ff9d4d' },
  { kelvin: 2700, label: 'Quente', color: '#ffbb73' },
  { kelvin: 3500, label: 'Neutra', color: '#ffd6a8' },
  { kelvin: 4500, label: 'Fria', color: '#ffeddc' },
  { kelvin: 6500, label: 'Luz do dia', color: '#eaf1ff' },
]
const MODOS: Modo[] = ['aquecer', 'resfriar', 'auto', 'desligado']

const FRASES: Record<Kind, string[]> = {
  luz: ['deixa a luz de leitura em 30 por cento', 'coloca a sala numa luz mais quente', 'apaga tudo da sala quando eu sair'],
  tomada: ['liga a tomada da TV', 'quanto essa tomada está consumindo agora?', 'desliga tudo do escritório às onze'],
  termostato: ['deixa o quarto em 21 graus', 'coloca o termostato no automático', 'está muito frio aqui'],
  interruptor: ['liga o interruptor da varanda', 'apaga a varanda'],
}

const STATUS = {
  offline: { label: 'offline', cls: 'tag-neutral' },
  auth: { label: 'credencial expirada', cls: 'tag-outline' },
  ok: { label: 'online', cls: 'tag-accent' },
}

const nearestKelvin = (k: number) =>
  KELVINS.reduce((a, b) => (Math.abs(b.kelvin - k) < Math.abs(a.kelvin - k) ? b : a))
const choice = (active: boolean) => `btn ${active ? 'btn-primary' : 'btn-secondary'}`

export function DevicePage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { data, setData, error, loading } = useAsync(async () => {
    const [device, rooms] = await Promise.all([devicesApi.get(Number(id)), roomsApi.list()])
    return { device, rooms }
  }, [id])

  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null)
  const [removing, setRemoving] = useState(false)
  const [draftBrilho, setDraftBrilho] = useState<number | null>(null)
  const brilhoTimer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(brilhoTimer.current), [])

  const back = (
    <button type="button" className="btn btn-ghost" onClick={() => navigate('/dispositivos')} style={{ marginBottom: 16 }}>
      ← Dispositivos
    </button>
  )
  if (loading) return <p className="page-state">Carregando…</p>
  if (!data) {
    return (
      <div>
        {back}
        <p className="page-state">{error}</p>
      </div>
    )
  }

  const { device, rooms } = data
  const setDevice = (device: Device) => setData((d) => d && { ...d, device })
  const s = device.state
  const status = STATUS[device.fault ?? 'ok']

  async function run(action: string, params: Record<string, unknown> = {}) {
    try {
      const result = await devicesApi.action(device.id, action, params)
      setDevice(result.device)
      setFeedback({ ok: true, text: result.message })
    } catch (err) {
      setFeedback({ ok: false, text: errorMessage(err) })
    }
  }
  function changeBrilho(value: number) {
    setDraftBrilho(value)
    window.clearTimeout(brilhoTimer.current)
    // ponytail: debounce de 300ms enquanto arrasta; um POST por parada do slider.
    brilhoTimer.current = window.setTimeout(() => run('ajustar_brilho', { brilho: value }).finally(() => setDraftBrilho(null)), 300)
  }
  async function reconnect() {
    try {
      setDevice(await devicesApi.reconnect(device.id))
      setFeedback({ ok: true, text: 'Conexão restabelecida — todas as ações voltaram a valer.' })
    } catch (err) {
      setFeedback({ ok: false, text: errorMessage(err) })
    }
  }
  async function remove() {
    try {
      await devicesApi.remove(device.id)
      navigate('/dispositivos', { replace: true })
    } catch (err) {
      setRemoving(false)
      setFeedback({ ok: false, text: errorMessage(err) })
    }
  }
  async function moveRoom(value: string) {
    try {
      setDevice(await devicesApi.move(device.id, value ? Number(value) : null))
    } catch (err) {
      setFeedback({ ok: false, text: errorMessage(err) })
    }
  }

  const brilho = draftBrilho ?? s.brilho ?? 0
  const preset = nearestKelvin(s.kelvin ?? 2700)
  const alvo = s.alvo ?? 21
  const meta = [device.room?.name, device.kind_label, device.driver.manufacturer, device.address, `#${device.id}`].filter(Boolean).join(' · ')

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        {back}
        <button type="button" className="btn btn-ghost" onClick={() => setRemoving(true)} style={{ marginBottom: 16 }}>
          Remover dispositivo
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2 style={{ margin: 0 }}>{device.name}</h2>
            <span className={`tag ${status.cls}`}>{status.label}</span>
          </div>
          <div className="text-muted" style={{ fontSize: 13 }}>{meta}</div>
        </div>
        <div className="field" style={{ flex: '1 1 180px', maxWidth: 240 }}>
          <label htmlFor="device-room">Cômodo</label>
          <select id="device-room" className="input" value={device.room?.id ?? ''} onChange={(e) => moveRoom(e.target.value)}>
            <option value="">Sem cômodo</option>
            {rooms.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
      </div>

      {device.fault && (
        <Blueprint style={{ padding: 14, margin: '16px 0', borderColor: 'var(--color-accent-700)', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div className="heading" style={{ fontSize: 17, color: 'var(--color-accent-800)' }}>
              {device.fault === 'offline' ? 'Dispositivo offline' : 'Credencial expirada'}
            </div>
            <div style={{ fontSize: 14 }}>
              {device.fault === 'offline'
                ? `O ${device.driver.manufacturer} não responde. executar_acao devolve erro explícito ao agente em vez de falhar em silêncio.`
                : `O refresh token de ${device.driver.manufacturer} foi rejeitado (401). Reautenticar restabelece todas as ações deste driver.`}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn btn-primary blueprint" onClick={reconnect} style={{ padding: '8px 14px' }}>
            <Corners />
            {device.fault === 'offline' ? 'Testar conexão' : 'Reautenticar'}
          </button>
        </Blueprint>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,300px),1fr))', gap: 'clamp(16px,3vw,26px)', marginTop: 22, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <Blueprint style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
              <h6 style={{ margin: 0, color: 'var(--color-accent-700)' }}>Controle</h6>
              <div style={{ flex: '1 1 0', minWidth: 0 }} />
              {device.kind !== 'termostato' && (
                <div className="seg" style={{ flex: '0 1 auto' }}>
                  <button type="button" className={choice(s.ligado === false)} onClick={() => run('desligar')} style={{ border: 0, minWidth: 88, minHeight: 42 }}>
                    Desligado
                  </button>
                  <button type="button" className={choice(s.ligado === true)} onClick={() => run('ligar')} style={{ border: 0, borderLeft: '1px solid var(--color-divider)', minWidth: 88, minHeight: 42 }}>
                    Ligado
                  </button>
                </div>
              )}
            </div>

            {device.kind === 'luz' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,150px),1fr))', gap: 'clamp(16px,3vw,24px)', alignItems: 'start' }}>
                <div>
                  <Blueprint style={{ height: 150, display: 'grid', placeItems: 'center', background: 'var(--color-neutral-900)' }}>
                    <div
                      style={{
                        width: 96, height: 96, borderRadius: '50%', filter: 'blur(2px)',
                        background: preset.color,
                        opacity: s.ligado ? 0.28 + (0.72 * brilho) / 100 : 0.06,
                      }}
                    />
                  </Blueprint>
                  <div className="kicker" style={{ marginTop: 8 }}>{s.ligado ? `${preset.label} · ${brilho}%` : 'apagada'}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
                      <label className="kicker" htmlFor="brilho">Brilho</label>
                      <span className="heading" style={{ fontSize: 24, lineHeight: 1 }}>{brilho}%</span>
                    </div>
                    <input id="brilho" type="range" min={1} max={100} step={1} value={brilho} onChange={(e) => changeBrilho(Number(e.target.value))} style={{ width: '100%', accentColor: 'var(--color-accent)' }} />
                  </div>
                  <div>
                    <div className="kicker" style={{ marginBottom: 8 }}>Temperatura de cor</div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {KELVINS.map((k) => (
                        <button
                          key={k.kelvin}
                          type="button"
                          className={choice(k.kelvin === preset.kelvin)}
                          onClick={() => run('definir_temperatura_cor', { kelvin: k.kelvin })}
                          style={{ flexDirection: 'column', gap: 6, padding: '8px 10px', minWidth: 74 }}
                        >
                          <span style={{ width: '100%', height: 22, background: k.color, border: '1px solid var(--color-divider)' }} />
                          <span style={{ fontSize: 12 }}>{k.label}</span>
                          <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.75 }}>{k.kelvin}K</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {device.kind === 'termostato' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-secondary" aria-label="Diminuir" onClick={() => run('definir_temperatura', { celsius: Math.max(16, alvo - 0.5) })} style={{ width: 46, height: 46, fontSize: 22 }}>
                    −
                  </button>
                  <div style={{ textAlign: 'center', minWidth: 130 }}>
                    <div className="heading" style={{ fontSize: 'clamp(44px,11vw,62px)', lineHeight: 1 }}>{alvo}°</div>
                    <div className="text-muted" style={{ fontSize: 12 }}>ambiente {s.atual ?? '—'}°C</div>
                  </div>
                  <button type="button" className="btn btn-secondary" aria-label="Aumentar" onClick={() => run('definir_temperatura', { celsius: Math.min(30, alvo + 0.5) })} style={{ width: 46, height: 46, fontSize: 22 }}>
                    +
                  </button>
                </div>
                <div>
                  <div className="kicker" style={{ marginBottom: 8 }}>Modo</div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {MODOS.map((m) => (
                      <button key={m} type="button" className={choice(s.modo === m)} onClick={() => run('definir_modo', { modo: m })}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {device.kind === 'tomada' && (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 30, flexWrap: 'wrap' }}>
                <div>
                  <div className="kicker">Consumo agora</div>
                  <div className="heading" style={{ fontSize: 'clamp(36px,9vw,48px)', lineHeight: 1 }}>{s.ligado ? (s.watts ?? 0) : 0} W</div>
                </div>
                <button type="button" className="btn btn-secondary" onClick={() => run('ler_consumo')} style={{ minHeight: 36 }}>
                  Ler consumo
                </button>
              </div>
            )}

            {feedback && (
              <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid var(--color-divider)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className={`tag ${feedback.ok ? 'tag-accent' : 'tag-outline'}`}>{feedback.ok ? 'feito' : 'erro'}</span>
                <span style={{ fontSize: 14 }}>{feedback.text}</span>
              </div>
            )}
          </Blueprint>

          <div className="text-muted" style={{ fontSize: 12 }}>
            O assistente chega nos mesmos controles por <span className="mono">executar_acao</span>: {device.actions.map((a) => a.name).join(', ')}
          </div>
        </div>

        <Blueprint style={{ padding: 20 }}>
          <h6 style={{ margin: '0 0 4px', color: 'var(--color-accent-700)' }}>Peça ao seu assistente</h6>
          <p className="text-muted" style={{ fontSize: 12, margin: '0 0 16px' }}>
            Nada de frase decorada: o modelo interpreta o pedido e escolhe a ação certa neste dispositivo.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {FRASES[device.kind].map((f) => (
              <Blueprint key={f} style={{ padding: '12px 14px', fontSize: 14, background: 'var(--color-accent-100)', borderColor: 'var(--color-accent-300)' }}>
                “{f}”
              </Blueprint>
            ))}
          </div>
        </Blueprint>
      </div>

      {removing && (
        <Dialog
          title={`Remover ${device.name}?`}
          onClose={() => setRemoving(false)}
          actions={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setRemoving(false)}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={remove}>Remover</button>
            </>
          }
        >
          <p style={{ fontSize: 14, margin: 0 }}>
            Sai do console e de <span className="mono">listar_dispositivos</span>. O aparelho continua na sua rede e pode ser adicionado de novo pela busca.
          </p>
        </Dialog>
      )}
    </div>
  )
}
